import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Briefcase, Plus, X, Edit, Eye } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HRJobs = () => {
  const { getAuthHeader } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: 'Ahmedabad',
    type: 'Full-time',
    experience: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hr/jobs`, {
        headers: getAuthHeader()
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingJob) {
        await axios.put(
          `${BACKEND_URL}/api/hr/jobs/${editingJob.id}`,
          formData,
          { headers: getAuthHeader() }
        );
        alert('Job updated successfully!');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/hr/jobs`,
          formData,
          { headers: getAuthHeader() }
        );
        alert('Job created successfully!');
      }
      
      setShowForm(false);
      setEditingJob(null);
      setFormData({
        title: '',
        department: '',
        location: 'Ahmedabad',
        type: 'Full-time',
        experience: '',
        description: '',
        is_active: true
      });
      fetchJobs();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save job');
    }
  };

  const handleEdit = (job) => {
    setFormData({
      title: job.title,
      department: job.department,
      location: job.location,
      type: job.type,
      experience: job.experience,
      description: job.description,
      is_active: job.is_active
    });
    setEditingJob(job);
    setShowForm(true);
  };

  const handleDeactivate = async (jobId) => {
    if (!confirm('Are you sure you want to deactivate this job listing?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/hr/jobs/${jobId}`, {
        headers: getAuthHeader()
      });
      alert('Job deactivated successfully');
      fetchJobs();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to deactivate job');
    }
  };

  const activeJobs = jobs.filter(j => j.is_active);
  const inactiveJobs = jobs.filter(j => !j.is_active);

  if (loading) return <div>Loading jobs...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase size={28} />
            Job Listings Management
          </h2>
          <p className="text-gray-600 mt-1">Active: {activeJobs.length} | Inactive: {inactiveJobs.length}</p>
        </div>
        <Button onClick={() => {
          setShowForm(!showForm);
          setEditingJob(null);
          setFormData({
            title: '',
            department: '',
            location: 'Ahmedabad',
            type: 'Full-time',
            experience: '',
            description: '',
            is_active: true
          });
        }}>
          {showForm ? <X size={18} className="mr-2" /> : <Plus size={18} className="mr-2" />}
          {showForm ? 'Cancel' : 'New Job'}
        </Button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <Card className="border-primary">
          <CardHeader className="bg-primary/5">
            <CardTitle>{editingJob ? 'Edit Job Listing' : 'Create New Job Listing'}</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Job Title *</label>
                  <Input
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g. Senior Civil Engineer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Department *</label>
                  <Input
                    required
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    placeholder="e.g. Engineering"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Location *</label>
                  <Input
                    required
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Type *</label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Experience Required *</label>
                  <Input
                    required
                    value={formData.experience}
                    onChange={(e) => setFormData({...formData, experience: e.target.value})}
                    placeholder="e.g. 3-5 years"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Job Description *</label>
                <Textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={4}
                  placeholder="Detailed job description, responsibilities, and requirements..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active" className="text-sm font-medium">Active (visible on website)</label>
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="bg-primary hover:bg-primary/90">
                  {editingJob ? 'Update Job' : 'Create Job'}
                </Button>
                <Button type="button" variant="outline" onClick={() => {
                  setShowForm(false);
                  setEditingJob(null);
                }}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Active Jobs */}
      <Card>
        <CardHeader>
          <CardTitle>Active Job Listings ({activeJobs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {activeJobs.map((job) => (
              <div key={job.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-bold text-lg">{job.title}</h4>
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                      <Badge variant="outline">{job.type}</Badge>
                    </div>
                    <div className="grid md:grid-cols-3 gap-2 text-sm text-gray-600 mb-2">
                      <div><span className="font-medium">Department:</span> {job.department}</div>
                      <div><span className="font-medium">Location:</span> {job.location}</div>
                      <div><span className="font-medium">Experience:</span> {job.experience}</div>
                    </div>
                    <p className="text-sm text-gray-700 line-clamp-2">{job.description}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(job)}
                  >
                    <Edit size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeactivate(job.id)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <X size={16} className="mr-1" />
                    Deactivate
                  </Button>
                </div>
              </div>
            ))}
            {activeJobs.length === 0 && (
              <p className="text-center py-8 text-gray-500">No active job listings</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Inactive Jobs */}
      {inactiveJobs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-gray-600">Inactive Job Listings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {inactiveJobs.map((job) => (
                <div key={job.id} className="p-3 border rounded-lg bg-gray-50 opacity-60">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{job.title}</div>
                      <div className="text-sm text-gray-600">
                        {job.department} • {job.location} • {job.type}
                      </div>
                    </div>
                    <Badge className="bg-gray-200 text-gray-700">Inactive</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HRJobs;
